import React from 'react';
import styled from 'styled-components';
import { Form, ConfigProvider } from 'antd';
import { Button, Input, InputPassword, Colors } from 'basic-ui-lib';
import loginLogo from 'assets/logo.svg';
import loginBg from 'assets/login_bg.png';
import useLogin from '../../services/hooks/useLogin';
import { Navigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [form] = Form.useForm<LoginFormValues>();
  const { login, isLoading } = useLogin();

  const token = storage.token.get();
  if (token) {
    return <Navigate to="/" replace />;
  }

  const onFinish = (values: LoginFormValues) => {
    login({
      userName: values.email,
      password: values.password,
    });
  };

  return (
    <PageContainer>
      <LoginCard>
        <Logo src={loginLogo} alt="Ontiloo Logo" />
        {/* <Text
          title_h3
          semibold
          color="neutral.text.5"
          style={{ marginTop: 16 }}
        >
          Digital Signage
        </Text> */}

        <FormContainer>
          <ConfigProvider
            theme={{
              components: {
                Form: {
                  labelFontSize: 14,
                  labelColor: Colors.neutral.text[5],
                  verticalLabelPadding: '0 0 4px',
                },
              },
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
                <Input placeholder="Please enter..." />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                  { max: 32, message: 'Password cannot exceed 32 characters' },
                  {
                    whitespace: true,
                    message: 'Password cannot be empty spaces',
                  },
                ]}
              >
                <InputPassword placeholder="••••••••" />
              </Form.Item>

              <ButtonWrapper>
                <Button 
                  type="submit" 
                  type_second 
                  block 
                  className="sign-in-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </ButtonWrapper>
            </Form>
          </ConfigProvider>
        </FormContainer>
      </LoginCard>
    </PageContainer>
  );
};

export default LoginPage;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url(${loginBg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const LoginCard = styled.div`
  width: 540px;
  background: ${Colors.neutral.bg[1]};
  border-radius: 8px;
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 250px;
  height: 80px;
  object-fit: contain;
`;

const FormContainer = styled.div`
  width: 100%;
  margin-top: 32px;

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item:last-of-type {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-family: Poppins;
    font-weight: 500;
  }

  .ant-form-item-explain-error {
    font-family: Poppins;
    font-size: 12px;
    text-align: right;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 32px;

  .sign-in-btn {
    width: 100%;
    height: 56px;
  }
`;
